import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRxCollection } from 'rxdb/plugins/react';
import type {
    DiaryDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    ParcelDocType,
    ProjectDocType,
    TranslationDocType
} from '@ast-data/shared';
import { useToast } from '../../../components/toastContext';
import { downloadBlob } from '../../../utils/downloadFile';
import { confirmUnavailableMedia } from '../pdf/confirmUnavailableMedia';
import { useConfirm } from '../../../components/confirmContext';
import { createDeliverableTexts } from '../../translation/deliverableTexts';
import { confirmTranslationGaps } from '../../translation/confirmTranslationGaps';
import {
    collectDiaryExportData,
    diaryPdfFileName,
    type DiaryExportCollections
} from './exportData';
import { isEmptyScope, type ExportScope } from '../pdf/exportScope';
import { usePersonLabels } from '../../users/personLabels';
import { recordMeasureInfo } from '../../projects/projectTitle';

/**
 * Export Tagebuch days to one PDF and download it.
 *
 * `exportPdf(scope, busyKey)` renders a whole Maßnahme or a named set of days
 * into one document, chronologically, one day per sheet. Ids may span Maßnahmen
 * (the cross-project Auswertung ticks them there); each sheet names its own.
 *
 * `busyKey` is what `busyId` reports back while this runs — a project id where
 * a grid shows a per-row spinner, absent where a plain `busy` will do.
 *
 * No parts/merge machinery like the Befund documentation has: a season of days
 * is a couple of hundred sheets, which react-pdf holds in one tree.
 *
 * i18n is passed into the react-pdf document, which renders outside the
 * IntlProvider.
 */
export function useDiaryPdfExport() {
    const intl = useIntl();
    const { notify } = useToast();
    const { confirm } = useConfirm();
    const [busyId, setBusyId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // The sheets name people by their accounts, as the screens do.
    const personLabels = usePersonLabels();
    const projects = useRxCollection<ProjectDocType>('projects');
    const diaries = useRxCollection<DiaryDocType>('diaries');
    const parcels = useRxCollection<ParcelDocType>('parcels');
    const media = useRxCollection<MediaDocType>('media');
    const mediaLinks = useRxCollection<MediaLinkDocType>('media_links');
    const mediaGroups = useRxCollection<MediaGroupDocType>('media_groups');
    const translations = useRxCollection<TranslationDocType>('translations');

    const exportPdf = useCallback(
        async (scope: ExportScope, busyKey?: string) => {
            if (
                !projects ||
                !diaries ||
                !parcels ||
                !media ||
                !mediaLinks ||
                !mediaGroups ||
                isEmptyScope(scope)
            ) {
                return;
            }
            setBusyId(busyKey ?? null);
            setBusy(true);
            try {
                const collections: DiaryExportCollections = {
                    projects,
                    diaries,
                    parcels,
                    media,
                    mediaLinks,
                    mediaGroups
                };
                const deliverable = createDeliverableTexts(
                    translations,
                    projects
                );
                const data = await collectDiaryExportData({
                    collections,
                    scope,
                    labelOf: personLabels.labelOf,
                    deliverable
                });
                // Nothing printable: no such days, or their Maßnahme is not on
                // this device — and a sheet's whole head comes from the
                // project.
                if (data.days.length === 0) {
                    notify(
                        intl.formatMessage({ id: 'diaryPdf.empty' }),
                        'info'
                    );
                    return;
                }
                // Offline, pictures not yet on this device cannot print — ask
                // before rendering a PDF without them.
                if (
                    !(await confirmUnavailableMedia(
                        confirm,
                        intl,
                        data.days.flatMap((day) => day.media)
                    ))
                ) {
                    return;
                }
                if (
                    !(await confirmTranslationGaps(
                        confirm,
                        intl,
                        deliverable.gaps
                    ))
                ) {
                    return;
                }
                // The renderer arrives here, at the click — see renderPdf.
                const { renderDiaryPdf } = await import('./renderPdf');
                const blob = await renderDiaryPdf(data, intl);
                downloadBlob(
                    blob,
                    diaryPdfFileName(
                        data.days.map(
                            (day) =>
                                recordMeasureInfo(day.project, {
                                    recordedAt: day.diary.recordedAt
                                }).measureNumber
                        ),
                        data.days.map((day) => day.diary.recordedAt)
                    )
                );
            } catch (cause) {
                notify(intl.formatMessage({ id: 'diaryPdf.error' }), 'error');
                console.error('Diary PDF export failed', cause);
            } finally {
                setBusyId(null);
                setBusy(false);
            }
        },
        [
            personLabels,
            projects,
            diaries,
            parcels,
            media,
            mediaLinks,
            mediaGroups,
            translations,
            intl,
            notify,
            confirm
        ]
    );

    return { exportPdf, busyId, busy };
}
